import React, { useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';

import { Strategy } from '~/api-services/AIStrategyBuilderService/types';
import { FONT_FAMILIES, FONT_SIZE_S } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import i18n from '~/i18n';
import { Theme } from '~/theme';

import { strategyStartDate } from '../../utils/format';
import ActivityList from './ActivityList';
import LivePositions from './LivePositions';
import LiveOrders from './LiveOrders';
import LiveResults from './LiveResults';
import LiveSwaps from './LiveSwaps';
import StatusChangesList from './StatusChangesList';

const Tab = createMaterialTopTabNavigator();

type Props = {
    strategy: Strategy;
};

// Route names must stay distinct from the backtest DetailTabs set so the two navigators cannot
// collide. Commissions is omitted per the PO; its hook and row exist, so restoring it is one
// Tab.Screen here plus a LiveCommissions alongside LiveResults.
export default function StrategyLive({ strategy }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();

    // Defaults to the strategy's own start; the reports' rolling window would open empty for a
    // strategy enabled months ago.
    const initialFrom = useMemo(() => strategyStartDate(strategy), [strategy]);
    const [from, setFrom] = useState(initialFrom);
    const [to, setTo] = useState(() => new Date());

    const scope = { strategyId: strategy.id, from, to, setFrom, setTo };

    return (
        <Tab.Navigator
            screenOptions={{
                lazy: true,
                tabBarScrollEnabled: true,
                tabBarActiveTintColor: theme.tabIconSelected,
                tabBarInactiveTintColor: theme.tabIconDefault,
                tabBarIndicatorStyle: { backgroundColor: theme.tabIconSelected },
                tabBarLabelStyle: styles.tabLabel,
                tabBarStyle: styles.tabBar,
                tabBarItemStyle: styles.tabItem,
            }}
        >
            <Tab.Screen name="LivePositions" options={{ title: i18n.t('open-trades') }}>
                {() => <LivePositions strategyId={strategy.id} />}
            </Tab.Screen>
            <Tab.Screen name="LiveResults" options={{ title: i18n.t('result') }}>
                {() => <LiveResults {...scope} />}
            </Tab.Screen>
            <Tab.Screen name="LiveOrders" options={{ title: i18n.t('all-orders') }}>
                {() => <LiveOrders {...scope} />}
            </Tab.Screen>
            <Tab.Screen name="LiveSwaps" options={{ title: i18n.t('interest-swap') }}>
                {() => <LiveSwaps {...scope} />}
            </Tab.Screen>
            <Tab.Screen name="LiveActivity" options={{ title: i18n.t('ai-strategy-tab-activity') }}>
                {() => <ActivityList strategyId={strategy.id} />}
            </Tab.Screen>
            <Tab.Screen name="LiveStatusChanges" options={{ title: i18n.t('ai-strategy-tab-status-changes') }}>
                {() => <StatusChangesList strategyId={strategy.id} />}
            </Tab.Screen>
        </Tab.Navigator>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        tabLabel: {
            fontFamily: FONT_FAMILIES.LATO_BOLD,
            fontSize: scaleSize(FONT_SIZE_S),
            textTransform: 'none',
        },
        tabBar: {
            backgroundColor: theme.background,
        },
        tabItem: {
            paddingHorizontal: scaleSize(12),
            width: 'auto',
        },
    });
